import React, { Component } from 'react';
import { css } from 'emotion';
import { connect } from 'react-redux';

import { fetchIntentSearchResults } from '../../../../actions/DiscoAdsActions';

import Modal from '../../../../components/Modal';
import DiscoAdsIntentListWrapper from './DiscoAdsIntentListWrapper';
import DiscoAdsButtonsWrapper from '../CreatorComponents/DiscoAdsCreatorButtonsWrapper';

import {
  getIntentSearchResults,
  getDefaultBid,
  getAppName,
  getTargetCountriesForDiscoAdsIntents,
  isPageLoading
} from '../../selectors/pageStateSelectors';

import {
  DA_CREATOR_BUTTON_COPY,
  ACTIVE,
  INTENT_SEARCH_RESULTS,
  SELECTED_INTENTS
} from '../../discoAdsConstants';

import { sortIntentsByPopularity, isBidValid } from '../../discoAdsUtils';

import Colors from '../../../../../../styles/Colors';

const styles = {
  modalContainer: css({
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '25px'
  }),
  modalHeader: css({
    backgroundColor: Colors['@white-lilac'],
    color: Colors['@tundora'],
    fontSize: '22px',
    textAlign: 'center',
    paddingTop: '16px',
    paddingBottom: '16px',
    flex: '1'
  }),
  modalContentContainer: css({
    display: 'flex',
    flex: '1',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginTop: '10px'
  })
};

const extractIntentFromSource = ({ intentId, source }) => {
  return source.filter(intent => intent.id !== intentId);
};

class DiscoAdsIntentsModal extends Component {
  state = {
    query: '',
    intentSearchResults: [],
    selectedIntents: this.props.selectedIntents
  };

  componentDidMount() {
    this._handleFetchIntentSearchResults();
  }

  componentDidUpdate(prevProps) {
    const { intentSearchResults } = this.props;
    if (prevProps.intentSearchResults !== intentSearchResults) {
      const sortedResults = sortIntentsByPopularity(intentSearchResults);
      this.setState({
        intentSearchResults: sortedResults
      });
    }
  }

  _handleFetchIntentSearchResults = () => {
    const { appName, fetchIntentSearchResults, targetCountries } = this.props;
    const { query, selectedIntents } = this.state;
    const lowerCaseQuery = query.toLowerCase();
    const payload = {
      targetCountries,
      appName,
      query: lowerCaseQuery,
      selectedIntentIds: selectedIntents.map(intent => intent.id)
    };
    fetchIntentSearchResults(payload);
  };

  _onSearchBarQueryChange = query => {
    this.setState({
      query
    });
    this._handleFetchIntentSearchResults();
  };

  _addIntentToSelectedIntents = intent => [
    intent,
    ...this.state.selectedIntents
  ];

  _hydrateIntentWithDefaultBid = intentId => {
    const { defaultBid } = this.props;
    const { intentSearchResults } = this.state;
    const selectedIndex = intentSearchResults.findIndex(
      intent => intent.id === intentId
    );
    const selectedIntent = intentSearchResults[selectedIndex];
    const selectedIntentWithBidData = Object.assign(selectedIntent, {
      max_cpc: defaultBid,
      bidIsValid: true,
      status: ACTIVE
    });
    return selectedIntentWithBidData;
  };

  _handleAddIntentToSelected = ({ intentId, selectedIntentWithBidData }) => {
    const { intentSearchResults } = this.state;
    const filteredSearchResults = extractIntentFromSource({
      intentId,
      source: intentSearchResults
    });
    const newSelectedIntents = this._addIntentToSelectedIntents(
      selectedIntentWithBidData
    );
    this.setState({
      intentSearchResults: filteredSearchResults,
      selectedIntents: newSelectedIntents
    });
  };

  _onIntentSearchResultClick = intentId => {
    const selectedIntentWithBidData = this._hydrateIntentWithDefaultBid(
      intentId
    );
    this._handleAddIntentToSelected({ intentId, selectedIntentWithBidData });
  };

  _handleRemoveIntentFromSelected = intentId => {
    const { selectedIntents } = this.state;
    const newSelectedIntents = extractIntentFromSource({
      intentId,
      source: selectedIntents
    });
    this.setState({
      selectedIntents: newSelectedIntents
    });
  };

  _handleIntentBidChange = ({ intentId, bid }) => {
    const { selectedIntents } = this.state;
    const updateIndex = selectedIntents.findIndex(
      intent => intent.id === intentId
    );
    const targetIntent = selectedIntents[updateIndex];
    const bidWithoutDollarSign = bid[0] === '$' ? bid.slice(1) : bid;
    const bidData = {
      max_cpc: `$${bidWithoutDollarSign}`,
      bidIsValid: isBidValid(bidWithoutDollarSign)
    };
    const intentWithNewBid = Object.assign({}, targetIntent, bidData);
    const updatedSelectedIntents = [
      ...selectedIntents.slice(0, updateIndex),
      intentWithNewBid,
      ...selectedIntents.slice(updateIndex + 1)
    ];
    this.setState({
      selectedIntents: updatedSelectedIntents
    });
  };

  _allBidsAreValid = () => {
    return this.state.selectedIntents.reduce((acc, intent) => {
      return acc && intent.bidIsValid;
    }, true);
  };

  _handleSaveAndCloseModal = () => {
    const { onSaveSelectedIntents, onOpenCloseIntentsModal } = this.props;
    onSaveSelectedIntents(this.state.selectedIntents);
    onOpenCloseIntentsModal();
  };

  render() {
    const { modalContainer, modalHeader, modalContentContainer } = styles;
    const {
      onOpenCloseIntentsModal,
      showIntentsModal,
      pageIsLoading
    } = this.props;
    const { intentSearchResults, selectedIntents, query } = this.state;
    return (
      <Modal
        open={showIntentsModal}
        testid="disco-ads-intents"
        onClose={onOpenCloseIntentsModal}
      >
        <div className={modalContainer}>
          <div className={modalHeader}>Add Intents to an Ad Group</div>
          <div className={modalContentContainer}>
            <DiscoAdsIntentListWrapper
              data={intentSearchResults}
              type={INTENT_SEARCH_RESULTS}
              userQuery={query}
              onSearchBarQueryChange={this._onSearchBarQueryChange}
              onIntentClick={this._onIntentSearchResultClick}
              pageIsLoading={pageIsLoading}
            />
            <DiscoAdsIntentListWrapper
              data={selectedIntents}
              type={SELECTED_INTENTS}
              onIntentClick={this._handleRemoveIntentFromSelected}
              onIntentBidChange={this._handleIntentBidChange}
            />
          </div>
          <DiscoAdsButtonsWrapper
            onCancelClick={onOpenCloseIntentsModal}
            saveButtonCopy={DA_CREATOR_BUTTON_COPY.SAVE}
            onSaveClick={this._handleSaveAndCloseModal}
            saveAndContinueButtonIsDisabled={
              !selectedIntents.length || !this._allBidsAreValid()
            }
          />
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  appName: getAppName(state),
  intentSearchResults: getIntentSearchResults(state),
  defaultBid: getDefaultBid(state),
  targetCountries: getTargetCountriesForDiscoAdsIntents(state),
  pageIsLoading: isPageLoading(state)
});

const mapDispatchToProps = dispatch => ({
  fetchIntentSearchResults: payload =>
    dispatch(fetchIntentSearchResults(payload))
});

const ConnectedDiscoAdsIntentsModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(DiscoAdsIntentsModal);

export default ConnectedDiscoAdsIntentsModal;
